import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    Receipt,
    Store as StoreIcon,
    AlertTriangle,
    UserCircle2,
    CirclePlus,
    X,
    User,
    ClipboardPenLine,
    Calendar,
    Currency,
    Package,
    Eye
} from 'lucide-react';
import { useToast } from '../hooks/useToast';

// Assuming these components are available in the specified path.
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { DataTable2 } from '../components/ui/DataTable2';
import Modal from '../components/ui/Modal';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Input from '../components/ui/Input';
import Checkbox from '../components/ui/Checkbox';

// API helper functions
import { fetchUtilCustomers, fetchCatentriesForStore, fetchCurrencies, client, API_BASE_PATH, fetchCustomerReturns, currencyCodeToSymbolMap, fetchOrderByStoreAndIdempotency } from '../apiHelper';

/**
 * CustomerReturns Component
 *
 * This component is responsible for handling customer returns. It displays key information
 * and provides a button to open a modal where the actual return form is located.
 */
const CustomerReturns = () => {
    // Access the location object to get state data passed from a previous route.
    const location = useLocation();
    const navigate = useNavigate();

    // State to hold the store ID and name received from the previous page.
    const [storeId, setStoreId] = useState(null);
    const [storeName, setStoreName] = useState(null);
    // State to hold the logged-in user object from local storage.
    const [user, setUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // State for recent returns data for the table
    const [recentReturns, setRecentReturns] = useState([]);

    // New state for the "View Items" modal
    const [isViewItemsModalOpen, setIsViewItemsModalOpen] = useState(false);
    const [selectedReturnDetails, setSelectedReturnDetails] = useState(null);


    // Form state for the customer return
    const [returnDetails, setReturnDetails] = useState({
        customer_id: null,
        order_id: '',
        return_date: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:mm
        currency: '',
        reason: '',
    });
    const [returnItems, setReturnItems] = useState([]);

    // State for SearchableSelectBox options and loading
    const [customerOptions, setCustomerOptions] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerError, setCustomerError] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [searchCustomerTerm, setSearchCustomerTerm] = useState('');

    const [catalogOptions, setCatalogOptions] = useState([]);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [catalogError, setCatalogError] = useState(null);

    const [currencyOptions, setCurrencyOptions] = useState([]);
    const [loadingCurrencies, setLoadingCurrencies] = useState(false);
    const [currencyError, setCurrencyError] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [orderIdInput, setOrderIdInput] = useState('');
    const [isPopulatingOrder, setIsPopulatingOrder] = useState(false);

    // Use a toast or a similar notification system for user feedback.
    const { showToast } = useToast()

    // Extract the storeId and storeName from the location state when the component mounts.
    useEffect(() => {
        const id = location.state?.storeId;
        const name = location.state?.storeName;
        if (id && name) {
            setStoreId(id);
            setStoreName(name);
            showToast(`Processing returns for store: ${name}`, 'success');
        } else {
            showToast("Error: No store information was provided for this return.", 'error');
        }
    }, [location.state, showToast]);

    // Effect to retrieve the user object from local storage when the component mounts.
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to use the return function.", "info");
            navigate('/login')
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast]);

    // New useEffect to fetch customer returns when storeId and user are available.
    useEffect(() => {
        const getRecentReturns = async () => {
            if (!storeId || !user) {
                return;
            }

            try {
                // 1. Construct the payload
                const payload = {
                    users_id: user.users_id,
                    logon_id: user.logon_id,
                    hpwc: user.hpwc,
                    store_id: storeId,
                    offset: 0,
                    limit: 100,
                };

                // 2. Pass the payload to fetchCustomerReturns
                const returnsResult = await fetchCustomerReturns(payload);

                // 3. Transform and set the data for the DataTable
                if (returnsResult && Array.isArray(returnsResult)) {
                    // We store the full item object to be used for the "View Items" modal
                    const formattedReturns = returnsResult.map(item => ({
                        ...item, // Store the full object for easy access to nested data
                        storeIdentifier: item.store?.identifier || 'N/A',
                        creatorName: `${item.creator?.user?.first_name || ''} ${item.creator?.user?.last_name || ''}`.trim() || 'N/A',
                        customerName: `${item.customer?.user?.first_name || ''} ${item.customer?.user?.last_name || ''}`.trim() || 'N/A',
                        orderId: item.order?.idempotency_key || 'N/A',
                        // Use the currencyCodeToSymbolMap to get the correct symbol
                        totalAmountFormatted: `${currencyCodeToSymbolMap[item.currency] || item.currency || ''} ${item.total_amount || 0}`,
                        reason: item.reason || 'No reason provided',
                        returnDate: new Date(item.return_date).toLocaleDateString(),
                    }));
                    setRecentReturns(formattedReturns);
                } else {
                    setRecentReturns([]);
                }

            } catch (err) {
                console.error("Failed to fetch recent returns:", err);
                const errorMessage = err.response?.data?.detail || `Failed to fetch recent returns: ${err.message || err}`;
                showToast(errorMessage, "error");
            }
        };

        getRecentReturns();
    }, [storeId, user, showToast]);


    // Fetch customers when the user object is available
    const fetchCustomers = useCallback(async (searchQuery = '') => {
        if (!user) return;
        setLoadingCustomers(true);
        setCustomerError(null);
        try {
            const fetchedCustomers = await fetchUtilCustomers(user, searchQuery);
            setCustomerOptions(fetchedCustomers.map(customer => ({
                label: `${customer.user?.first_name || ''} ${customer.user?.last_name || ''} (${customer.user?.credential?.logon_id || 'N/A'})`,
                value: customer.member_id,
                customerData: customer
            })));
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load customers: ${err.message || err}`;
            setCustomerError(errorMessage);
            setTimeout(() => showToast(errorMessage, 'error'), 0);
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, showToast]);

    useEffect(() => {
        if (isModalOpen && user) {
            fetchCustomers(searchCustomerTerm);
        }
    }, [isModalOpen, user, fetchCustomers, searchCustomerTerm]);


    // Fetch catalog entries for the store when storeId is available
    useEffect(() => {
        const fetchCatalogEntries = async () => {
            if (!storeId) return;
            setLoadingCatalog(true);
            setCatalogError(null);
            try {
                const fetchedLots = await fetchCatentriesForStore({ store_id: storeId });
                // Aggregate quantities for the same catentry_id
                const aggregatedItems = fetchedLots.reduce((acc, lot) => {
                    if (acc[lot.catentry_id]) {
                        acc[lot.catentry_id].current_quantity += lot.current_quantity;
                    } else {
                        acc[lot.catentry_id] = {
                            catentry_id: lot.catentry_id,
                            productname: lot.catentry?.productname,
                            partnumber: lot.catentry?.partnumber,
                            current_quantity: lot.current_quantity,
                            // Use the price from the first lot found, or handle as needed
                            listprice: lot.catentry?.listprice?.listprice,
                        };
                    }
                    return acc;
                }, {});
                const catalogItems = Object.values(aggregatedItems);

                setCatalogOptions(catalogItems.map(item => ({
                    label: `${item.productname} (SKU: ${item.partnumber}) - Qty: ${item.current_quantity}`,
                    value: item.catentry_id,
                    productData: item
                })));
            } catch (err) {
                const errorMessage = `Failed to load catalog entries: ${err.message || err}`;
                setCatalogError(errorMessage);
                setTimeout(() => showToast(errorMessage, 'error'), 0);
            } finally {
                setLoadingCatalog(false);
            }
        };
        if (isModalOpen && storeId) {
            fetchCatalogEntries();
        }
    }, [isModalOpen, storeId, showToast]);

    // Fetch currencies when the component mounts
    useEffect(() => {
        const fetchAllCurrencies = async () => {
            setLoadingCurrencies(true);
            setCurrencyError(null);
            try {
                const currenciesResult = await fetchCurrencies();
                // Correctly check if the result is an array before mapping
                if (Array.isArray(currenciesResult)) {
                    setCurrencyOptions(currenciesResult.map(c => ({
                        label: `${c.setccurr} - ${c.setcnote}`,
                        value: c.setccurr
                    })));
                } else {
                    throw new Error("API response for currencies was malformed or empty.");
                }
            } catch (err) {
                const errorMessage = `Failed to load currencies: ${err.message || err}`;
                setCurrencyError(errorMessage);
                setTimeout(() => showToast(errorMessage, 'error'), 0);
            } finally {
                setLoadingCurrencies(false);
            }
        };
        if (isModalOpen) {
            fetchAllCurrencies();
        }
    }, [isModalOpen, showToast]);


    // Recalculate total amount whenever return items change
    const totalAmount = useMemo(() => {
        return returnItems.reduce((sum, item) => sum + (parseFloat(item.price) * parseFloat(item.quantity)), 0).toFixed(2);
    }, [returnItems]);


    // Handlers for form inputs
    const handleReturnDetailsChange = (e) => {
        const { name, value } = e.target;
        setReturnDetails(prev => ({ ...prev, [name]: value }));
    };

    const handleReturnItemChange = (index, e) => {
        const { name, value, type, checked } = e.target;
        const newItems = [...returnItems];
        newItems[index] = {
            ...newItems[index],
            [name]: type === 'checkbox' ? checked : value
        };
        setReturnItems(newItems);
    };

    const handleAddItem = () => {
        setReturnItems(prev => [
            ...prev,
            { catentry_id: null, quantity: 1, price: 0, is_restockable: true }
        ]);
    };

    const handleRemoveItem = (index) => {
        setReturnItems(prev => prev.filter((_, i) => i !== index));
    };


    const handleOpenModal = () => {
        setIsModalOpen(true);
        // Reset form state when the modal is opened
        setReturnDetails({
            customer_id: null,
            order_id: '',
            return_date: new Date().toISOString().slice(0, 16),
            currency: '',
            reason: '',
        });
        setReturnItems([]);
        setSelectedCustomer(null);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    // New handler for the DataTable2 row actions
    const handleRowAction = (actionKey, row) => {
        if (actionKey === 'view-items') {
            setSelectedReturnDetails(row);
            setIsViewItemsModalOpen(true);
        }
        // Could add more actions here in the future
    };

    // Close the new "View Items" modal
    const handleCloseViewItemsModal = () => {
        setIsViewItemsModalOpen(false);
        setSelectedReturnDetails(null);
    };

    // Recalculate total items and total amount for the summary
    const returnItemsSummary = useMemo(() => {
        if (!selectedReturnDetails) return { totalItems: 0, totalAmount: 0 };
        // Corrected to use `selectedReturnDetails.returnitems`
        const totalItems = selectedReturnDetails.returnitems?.reduce((sum, item) => sum + item.quantity, 0) || 0;
        // Corrected to use `selectedReturnDetails.returnitems`
        const totalAmount = selectedReturnDetails.returnitems?.reduce((sum, item) => sum + (item.quantity * parseFloat(item.price)), 0) || 0;
        return { totalItems, totalAmount: totalAmount.toFixed(2) };
    }, [selectedReturnDetails]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        // Form validation
        if (!returnDetails.customer_id) {
            showToast("Please select a customer.", 'error');
            setIsSubmitting(false);
            return;
        }
        if (returnItems.length === 0) {
            showToast("Please add at least one item to return.", 'error');
            setIsSubmitting(false);
            return;
        }

        // Convert the datetime-local string to a full ISO string with timezone information.
        const returnDateWithTimezone = new Date(returnDetails.return_date).toISOString();

        // Construct the final payload based on the Pydantic schemas
        const finalReturnPayload = {
            ...returnDetails,
            store_id: storeId,
            creator_id: user?.users_id,
            total_amount: parseFloat(totalAmount),
            return_date: returnDateWithTimezone, // Use the new string with timezone
            return_items: returnItems.map(item => {
                const product = catalogOptions.find(p => p.value === item.catentry_id);
                const productName = product?.productData?.productname || 'N/A';
                return {
                    catentry_id: item.catentry_id,
                    productname: productName, // Add the product name
                    quantity: parseFloat(item.quantity),
                    price: parseFloat(item.price),
                    is_restockable: item.is_restockable
                };
            })
        };

        // console.log("Submitting return payload:", finalReturnPayload);
        try {
            // console.log(finalReturnPayload)
            const response = await client.postData(`${API_BASE_PATH}/store/accept-returns`, finalReturnPayload)
            showToast("Return form submitted. Check the console for the payload.", 'success');
            setIsSubmitting(false);
            handleCloseModal(); // Close the modal after submission (or on success response)
        } catch (err) {
            console.error("Failed to save customer returns record:", err);
            const errorMessage = err.response?.data?.detail || `Failed to save customer returns record: ${err.message || err}`;
            showToast(errorMessage, "error")
            setIsSubmitting(false)
        }
    };

    const handlePopulateOrder = useCallback(async () => {
        if (!orderIdInput.trim() || !storeId) return;

        setIsPopulatingOrder(true);
        try {
            const payload = {
                store_id: storeId,
                idempotency_key: orderIdInput.trim(),
            };

            const orderData = await fetchOrderByStoreAndIdempotency(payload);

            if (!orderData) {
                showToast("Order not found.", "error");
                return;
            }

            // 1. Set Customer
            if (orderData.customer?.member_id) {
                const customerObj = {
                    member_id: orderData.customer.member_id,
                    user: {
                        first_name: orderData.customer.user?.first_name || '',
                        last_name: orderData.customer.user?.last_name || '',
                        credential: { logon_id: orderData.customer.user?.credential?.logon_id || '' }
                    }
                };
                const customerOption = {
                    label: `${customerObj.user?.first_name} ${customerObj.user?.last_name} (${customerObj.user?.credential?.logon_id})`,
                    value: customerObj.member_id,
                    customerData: customerObj
                };
                setSelectedCustomer(customerObj);
                setReturnDetails(prev => ({ ...prev, customer_id: customerObj.member_id }));
                // Preload into customer options if not already there
                setCustomerOptions(prev => {
                    const exists = prev.some(opt => opt.value === customerObj.member_id);
                    return exists ? prev : [...prev, customerOption];
                });
            }

            // 2. Set Currency
            const currencyCode = orderData.order_currency?.setccurr || orderData.currency;
            if (currencyCode) {
                setReturnDetails(prev => ({ ...prev, currency: currencyCode }));
            }

            // 3. Populate Return Items
            if (Array.isArray(orderData.orderitems) && orderData.orderitems.length > 0) {
                // Fetch catalog to match catentry_id → product data
                const catalogLots = await fetchCatentriesForStore({ store_id: storeId });
                const catalogMap = catalogLots.reduce((map, lot) => {
                    if (!map[lot.catentry_id]) {
                        map[lot.catentry_id] = {
                            productname: lot.catentry?.productname,
                            partnumber: lot.catentry?.partnumber,
                            listprice: lot.catentry?.listprice?.listprice,
                            current_quantity: 0
                        };
                    }
                    map[lot.catentry_id].current_quantity += lot.current_quantity;
                    return map;
                }, {});

                const populatedItems = orderData.orderitems.map(item => ({
                    catentry_id: item.catentry_id,
                    quantity: item.quantity,
                    price: parseFloat(item.price).toFixed(2),
                    is_restockable: true, // default; can be enhanced later
                    // For display in select box
                    productData: catalogMap[item.catentry_id] || {
                        productname: item.catentry?.productname || 'Unknown Product',
                        partnumber: item.catentry?.partnumber || 'N/A'
                    }
                }));

                setReturnItems(populatedItems);
            }

            showToast("Order populated successfully!", "success");
        } catch (err) {
            const errorMsg = err.response?.data?.detail || "Failed to populate order.";
            showToast(errorMsg, "error");
            console.error("Populate order error:", err);
        } finally {
            setIsPopulatingOrder(false);
        }
    }, [orderIdInput, storeId, showToast]);

    // Conditional rendering if no storeId is found.
    if (!storeId || !storeName) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <AlertTriangle size={32} className="mb-2" />
                <p className="text-lg mb-4">Error: Could not load store information for returns.</p>
                <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            {/* Header Section */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <Receipt size={40} className="text-blue-600" />
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary">
                            Customer Returns
                        </h1>
                        <p className="text-md text-gray-500 flex items-center gap-1">
                            <StoreIcon size={16} />
                            Store: {storeName}
                        </p>
                        {user && (
                            <p className="text-sm text-gray-400 flex items-center gap-1 mt-1">
                                <UserCircle2 size={16} />
                                Logged in as: {user.logon_id}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={handleOpenModal}
                        variant="primary"
                        className="flex items-center gap-2"
                    >
                        <CirclePlus size={20} /> Accept Returns
                    </Button>
                    <Button onClick={() => navigate(-1)} variant="secondary" className="flex items-center gap-2">
                        <ChevronLeft size={16} /> Go Back
                    </Button>
                </div>
            </div>

            {/* Main Content Area */}
            <Card className="p-6 mt-8">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Recent Returns</h3>
                <DataTable2
                    columns={[
                        { key: 'storeIdentifier', label: 'Store' },
                        { key: 'creatorName', label: 'Attendant' },
                        { key: 'customerName', label: 'Customer' },
                        { key: 'orderId', label: 'Order ID' },
                        { key: 'totalAmountFormatted', label: 'Total Amount' }, // Use the new formatted key
                        { key: 'reason', label: 'Reason' },
                        { key: 'returnDate', label: 'Return Date' }
                    ]}
                    data={recentReturns}
                    rowsPerPage={5}
                    emptyMessage="No recent returns for this store."
                    // New props for row actions
                    rowActions={[
                        { label: "View Items", key: "view-items", icon: <Eye size={16} /> }
                    ]}
                    onAction={handleRowAction}
                />
            </Card>

            {/* The Return Form Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={`Customer Returns for ${storeName || 'Store'}`}
                size="wide"
            >
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Main Return Form */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Customer Search */}
                        <SearchableSelectBox
                            label="Select Customer"
                            id="customer-select"
                            name="customer-select"
                            value={selectedCustomer?.member_id || ''}
                            onChange={(e) => {
                                const selectedOption = customerOptions.find(opt => opt.value === parseInt(e.target.value));
                                setSelectedCustomer(selectedOption?.customerData || null);
                                setReturnDetails(prev => ({ ...prev, customer_id: selectedOption?.value || null }));
                            }}
                            options={customerOptions}
                            placeholder="Search or select a customer"
                            onSearchChange={setSearchCustomerTerm}
                            loading={loadingCustomers}
                            error={customerError}
                            icon={<User size={18} />}
                        />
                        {/* Order ID Input */}
                        <Input
                            label="Order ID (Optional)"
                            id="order-id"
                            name="order_id"
                            type="text"
                            value={returnDetails.order_id}
                            // onChange={handleReturnDetailsChange}
                            onChange={(e) => {
                                const value = e.target.value;
                                setReturnDetails(prev => ({ ...prev, order_id:value }));
                                setOrderIdInput(value);
                            }}
                            placeholder="e.g., #12345"
                            icon={<ClipboardPenLine size={18} />}
                        />
                        <div className="mt-1">
                            {orderIdInput.trim() && (
                                <button 
                                    type="button"
                                    onClick={handlePopulateOrder}
                                    disabled={isPopulatingOrder || !storeId}
                                    className={`
                                        text-sm font-medium text-blue-600 hover:text-blue-800
                                        disabled:text-gray-400 disabled:cursor-not-allowed
                                        underline underline-offset-2 transition-colors
                                        ${isPopulatingOrder ? 'animate-pulse' : ''}
                                    `}
                                >
                                    {isPopulatingOrder ? 'Populating...' : 'Populate Order using this Order ID'}
                                </button>
                            )}
                        </div>
                        {/* Return Date Input */}
                        <Input
                            label="Return Date & Time"
                            id="return-date"
                            name="return_date"
                            type="datetime-local"
                            value={returnDetails.return_date}
                            onChange={handleReturnDetailsChange}
                            icon={<Calendar size={18} />}
                        />
                        {/* Currency Select */}
                        <SearchableSelectBox
                            label="Currency"
                            id="currency-select"
                            name="currency"
                            value={returnDetails.currency}
                            onChange={handleReturnDetailsChange}
                            options={currencyOptions}
                            placeholder="Select currency"
                            loading={loadingCurrencies}
                            error={currencyError}
                            icon={<Currency size={18} />}
                        />
                    </div>

                    {/* Reason for Return Input */}
                    <Input
                        label="Reason for Return (Optional)"
                        id="reason"
                        name="reason"
                        type="textarea"
                        value={returnDetails.reason}
                        onChange={handleReturnDetailsChange}
                        placeholder="Provide a reason for the return..."
                    />

                    <hr className="my-6" />

                    {/* Return Items Section */}
                    <div className="flex justify-between items-center">
                        <h3 className="text-xl font-semibold text-text-primary">Items to Return</h3>
                        <Button
                            type="button"
                            onClick={handleAddItem}
                            variant="primary"
                            className="flex items-center gap-2"
                        >
                            <CirclePlus size={20} /> Add Item
                        </Button>
                    </div>

                    <div className="space-y-4 max-h-64 overflow-y-auto">
                        {returnItems.length === 0 && (
                            <p className="text-center text-gray-500 py-4 border border-dashed rounded-lg">No items added yet.</p>
                        )}
                        {returnItems.map((item, index) => (
                            <Card key={index} className="p-4 border-l-4 border-indigo-500 relative">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveItem(index)}
                                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                                >
                                    <X size={16} />
                                </Button>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                                    <SearchableSelectBox
                                        label="Product"
                                        id={`product-${index}`}
                                        name="catentry_id"
                                        value={item.catentry_id || ''}
                                        onChange={(e) => handleReturnItemChange(index, e)}
                                        options={catalogOptions}
                                        placeholder="Select a product"
                                        loading={loadingCatalog}
                                        error={catalogError}
                                        icon={<Package size={18} />}
                                    />
                                    <Input
                                        label="Quantity"
                                        id={`quantity-${index}`}
                                        name="quantity"
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => handleReturnItemChange(index, e)}
                                        min="1"
                                    />
                                    <Input
                                        label="Refund Price"
                                        id={`price-${index}`}
                                        name="price"
                                        type="number"
                                        value={item.price}
                                        onChange={(e) => handleReturnItemChange(index, e)}
                                        min="0"
                                    />
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            label={item.is_restockable ? 'Restockable' : 'Not Restockable'}
                                            id={`restockable-${index}`}
                                            name="is_restockable"
                                            checked={item.is_restockable}
                                            onChange={(e) => handleReturnItemChange(index, e)}
                                        />
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>

                    <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg shadow-inner">
                        <p className="text-lg font-semibold text-gray-700">Total Refund Amount:</p>
                        <p className="text-2xl font-bold text-blue-600">
                            {currencyCodeToSymbolMap[returnDetails.currency] || returnDetails.currency} {totalAmount}
                        </p>
                    </div>

                    <div className="pt-4">
                        <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={isSubmitting || returnItems.length === 0 || !selectedCustomer || !returnDetails.currency}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Return'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* New Modal for Viewing Return Items */}
            <Modal
                isOpen={isViewItemsModalOpen}
                onClose={handleCloseViewItemsModal}
                title={`Return Details`}
                className="max-w-6xl"
                size="wide"
            >
                {selectedReturnDetails && (
                    <div className="space-y-6">
                        {/* Header for the modal content */}
                        <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg shadow-sm border border-gray-200">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Order ID</p>
                                {/* Updated to use the orderId for display */}
                                <p className="text-base font-semibold text-gray-800">#{selectedReturnDetails.orderId}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Return Date</p>
                                <p className="text-base font-semibold text-gray-800">{selectedReturnDetails.returnDate}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Attendant Name</p>
                                <p className="text-base font-semibold text-gray-800">{selectedReturnDetails.creatorName}</p>
                            </div>
                        </div>

                        {/* Table for items */}
                        <div className="overflow-x-auto border rounded-lg shadow-sm">
                            <table className="min-w-full text-sm text-gray-800 table-auto border-collapse">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Product Name</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Part Number</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Quantity</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Price</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Restockable</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {/* Corrected property name from return_items to returnitems */}
                                    {selectedReturnDetails.returnitems?.length > 0 ? (
                                        // Corrected property name from return_items to returnitems
                                        selectedReturnDetails.returnitems.map((item, index) => (
                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3">{item.catentry?.productname || item.productname || 'N/A'}</td>
                                                <td className="px-4 py-3">{item.catentry?.partnumber || item.partnumber || 'N/A'}</td>
                                                <td className="px-4 py-3">{item.quantity}</td>
                                                <td className="px-4 py-3">
                                                    <span className="font-semibold">
                                                        {currencyCodeToSymbolMap[selectedReturnDetails.currency] || selectedReturnDetails.currency} {parseFloat(item.price).toFixed(2)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${item.is_restockable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                        {item.is_restockable ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4 text-gray-500">No items were found for this return.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Summary section */}
                        <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                            <p className="text-lg font-semibold text-gray-700">Total Refund Summary:</p>
                            <div className="flex items-center gap-8">
                                <div className="text-center">
                                    <p className="text-sm font-medium text-gray-500">Total Items</p>
                                    <p className="text-xl font-bold text-gray-800">{returnItemsSummary.totalItems}</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-medium text-gray-500">Total Amount</p>
                                    <p className="text-xl font-bold text-blue-600">
                                        {currencyCodeToSymbolMap[selectedReturnDetails.currency] || selectedReturnDetails.currency} {returnItemsSummary.totalAmount}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end">
                            <Button onClick={handleCloseViewItemsModal} variant="secondary">
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CustomerReturns;
